import { PuzzleEngine } from "./puzzle-engine.ts";
import { GameView } from "./render/game-view.ts";
import { MapView } from "./render/map-view.ts";
import { MenuView, type ManifestEntry } from "./render/menu-view.ts";
import { LangToggle, regionLabel, t } from "./render/i18n.ts";
import { ThemeToggle } from "./render/theme.ts";
import { bestRecordKey, StorageService } from "./storage-service.ts";
import type { Difficulty, RegionData, RegionScope } from "./types.ts";

const pick = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

// 隱私模式或封鎖 storage 的瀏覽器仍應能遊玩，只是無法保存紀錄
let storage: StorageService | null = null;
try {
  storage = new StorageService();
} catch {
  storage = null;
}

// 語言必須先於主題初始化：主題按鈕的文字本身就要翻譯
// 切換語言時靜態文字由 data-i18n 處理，動態產生的則各自 refresh
new LangToggle(
  [pick<HTMLButtonElement>("lang-menu"), pick<HTMLButtonElement>("lang-hud")],
  () => {
    theme.refresh();
    menu.refresh();
    game?.gameView.refresh();
    if (currentRegion) pick("scope").textContent = regionLabel(currentRegion);
  },
);

// 兩顆按鈕（選單一顆、HUD 一顆）共用同一個控制器，狀態與文字自動同步
const theme = new ThemeToggle([pick<HTMLButtonElement>("theme-menu"), pick<HTMLButtonElement>("theme-hud")]);

const manifest = await (await fetch("./data/manifest.json")).json();
const entries: ManifestEntry[] = manifest.files;

let game: { mapView: MapView; gameView: GameView } | null = null;
let currentRegion: RegionScope | null = null;

const menu = new MenuView(pick("menu"), entries, storage, startGame);

function teardown(): void {
  game?.gameView.destroy();
  game?.mapView.destroy();
  game = null;
}

function showMenu(): void {
  teardown();
  pick("game").hidden = true;
  history.replaceState(null, "", location.pathname);
  menu.show();
}

async function startGame(region: RegionScope, difficulty: Difficulty): Promise<void> {
  const file = `${region.toLowerCase()}.${difficulty.toLowerCase()}.json`;
  let data: RegionData;
  try {
    const res = await fetch(`./data/${file}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    data = await res.json();
  } catch {
    menu.showError(t("loadFailed", { file }));
    return;
  }

  teardown();
  menu.hide();
  pick("game").hidden = false;
  history.replaceState(null, "", `?region=${region.toLowerCase()}&difficulty=${difficulty.toLowerCase()}`);

  const engine = PuzzleEngine.create(data);
  const mapView = new MapView(pick("map"), pick<any>("overlay"));

  currentRegion = region;
  pick("scope").textContent = regionLabel(region);
  pick("best").textContent = (() => {
    try {
      return String(storage?.loadBestRecord(bestRecordKey(region, difficulty))?.bestScore ?? "—");
    } catch {
      return "—"; // 紀錄損毀時不擋開局，結算時再一併重置
    }
  })();

  const gameView = new GameView(
    engine,
    mapView,
    {
      tray: pick("tray"),
      timer: pick("timer"),
      progress: pick("progress"),
      mistakes: pick("mistakes"),
      hintButton: pick<HTMLButtonElement>("hint"),
      hintLabel: pick("hint-label"),
      pauseIcon: document.getElementById("pause-icon") as unknown as SVGUseElement,
      progressBar: pick("hud-bar").querySelector("i")!,
      pauseButton: pick<HTMLButtonElement>("pause"),
      moreButton: pick<HTMLButtonElement>("more"),
      morePanel: pick("more-menu"),
      resumeButton: pick<HTMLButtonElement>("resume"),
      quitButton: pick<HTMLButtonElement>("quit"),
      result: pick("result"),
    },
    storage,
    { onPlayAgain: () => startGame(region, difficulty), onBackToMenu: showMenu },
  );

  game = { mapView, gameView };
  // 供瀏覽器主控台檢查拖放座標與遊戲狀態；純前端無機敏資料，保留為開發便利
  (globalThis as any).__puzzle = { engine, mapView, storage };
}

const params = new URLSearchParams(location.search);
const deepLink = entries.find(
  (e) =>
    e.regionScope.toLowerCase() === params.get("region") &&
    e.difficulty.toLowerCase() === params.get("difficulty"),
);

if (deepLink) {
  menu.select(deepLink.regionScope, deepLink.difficulty);
  menu.hide();
  await startGame(deepLink.regionScope, deepLink.difficulty);
} else {
  showMenu();
}
